// AuthDialog.js
import React, { useState } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Tabs,
    Tab,
    Box,
    IconButton,
    InputAdornment,
    CircularProgress,
    Alert,
    Checkbox,
    FormControlLabel,
    Link,
    Typography,
} from '@mui/material';
import { Visibility, VisibilityOff, PersonAdd as PersonAddIcon } from '@mui/icons-material';
import AuthService from '../Auth/AuthService';
import { useNavigate } from 'react-router-dom';
import { LogInIcon } from 'lucide-react';

const AuthDialog = ({ open, onClose, onAuthSuccess, initialTab = 0 }) => {
    const [tab, setTab] = useState(initialTab);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    const [loginData, setLoginData] = useState({ username: '', password: '' });
    const [showLoginPassword, setShowLoginPassword] = useState(false);

    const [registerData, setRegisterData] = useState({
        email: '',
        mobile: '',
        password: '',
        confirmPassword: '',
        role: 'user',
    });
    const [showRegisterPassword, setShowRegisterPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [acceptTerms, setAcceptTerms] = useState(true); // Default checked
    const navigate = useNavigate();

    const handleTabChange = (event, newValue) => {
        setTab(newValue);
        setLoading(false);
        setError('');
        // Reset terms checkbox when switching to register tab
        if (newValue === 1) {
            setAcceptTerms(true);
        }
    };

    const handleLoginChange = (e) => {
        setLoginData({ ...loginData, [e.target.name]: e.target.value });
        setError('');
    };

    const handleRegisterChange = (e) => {
        setRegisterData({ ...registerData, [e.target.name]: e.target.value });
        setError('');
    };

    const handleTermsChange = (e) => {
        setAcceptTerms(e.target.checked);
        setError('');
    };

    const validateRegister = () => {
        // if (!registerData.name.trim()) return 'Name is required';
        if (!registerData.email.trim()) return 'Email is required';
        if (!/^\S+@\S+\.\S+$/.test(registerData.email)) return 'Invalid email format';
        if (!registerData.mobile.trim()) return 'Mobile number is required';
        if (!/^\d{10}$/.test(registerData.mobile)) return 'Mobile must be 10 digits';
        if (!registerData.password) return 'Password is required';
        if (registerData.password.length < 6) return 'Password must be at least 6 characters';
        if (registerData.password !== registerData.confirmPassword) return 'Passwords do not match';
        if (!acceptTerms) return 'You must accept the Terms and Conditions to register';
        return null;
    };

    const handleLogin = async () => {
        if (!loginData.username.trim() || !loginData.password.trim()) {
            setError('Please enter both username and password');
            return;
        }
        setLoading(true);
        setError('');
        const rest = AuthService.pathUrl("POST", "/auth/login");
        const tokendata = await AuthService.registerLogin(rest, loginData);

        if (tokendata?.is_verfied === true || tokendata?.roles === "SUPPERADMIN") {
            navigate("/");
        } else if (tokendata?.is_verfied === false)
            navigate("/verify");
        else if (tokendata.accessToken !== undefined)
            navigate("/loginview");


        setTimeout(() => {
            setLoading(false);
            onAuthSuccess({ name: loginData?.username, role: tokendata?.roles });
            onClose();
        }, 1000);
        window.location.reload();
    };

    const handleRegister = async () => {
        const validationError = validateRegister();
        if (validationError) {
            setError(validationError);
            return;
        }
        setLoading(true);
        setError('');
        const data = {
            email: registerData.email,
            mobile: registerData.mobile,
            password: registerData.password,
            roleType: 'ADMIN',
            loginType: "SCHOOL",
            isActive: true,
            acceptTerms: true,
        };

        const rest = AuthService.pathUrl("POST", "/auth/register")
        const tokendata = await AuthService.registerLogin(rest, data);

        if (tokendata.status === 404) {
            setError(tokendata.message);
            setLoading(false);
            return;
        }
        if (tokendata?.is_verfied === true)
            navigate("/")
        else if (tokendata?.is_verfied === false)
            navigate("/verify");
        else if (tokendata.accessToken !== undefined)
            navigate("/loginview");
        // else if (tokendata.status === 404)

        setTimeout(() => {
            setLoading(false);
            onAuthSuccess({ name: registerData.name, email: registerData.email, role: registerData.role });
            onClose();
            window.location.reload();
        }, 1000);
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>
                <Tabs value={tab} onChange={handleTabChange} variant="fullWidth">
                    <Tab label="Login" icon={<LogInIcon />} iconPosition="start" />
                    <Tab label="Register" icon={<PersonAddIcon />} iconPosition="start" />
                </Tabs>
            </DialogTitle>
            <DialogContent dividers>
                {error && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        {error}
                    </Alert>
                )}
                {tab === 0 ? (
                    // Login form
                    <Box component="form" sx={{ mt: 1 }}>
                        <TextField
                            margin="normal"
                            required
                            fullWidth
                            label="Username / Email"
                            name="username"
                            autoComplete="username"
                            autoFocus
                            value={loginData.username}
                            onChange={handleLoginChange}
                            disabled={loading}
                        />
                        <TextField
                            margin="normal"
                            required
                            fullWidth
                            label="Password"
                            name="password"
                            type={showLoginPassword ? 'text' : 'password'}
                            autoComplete="current-password"
                            value={loginData.password}
                            onChange={handleLoginChange}
                            disabled={loading}
                            slotProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton
                                            onClick={() => setShowLoginPassword(!showLoginPassword)}
                                            edge="end"
                                        >
                                            {showLoginPassword ? <VisibilityOff /> : <Visibility />}
                                        </IconButton>
                                    </InputAdornment>
                                ),
                            }}
                        />
                    </Box>
                ) : (
                    // Register form
                    <Box component="form" sx={{ mt: 1 }}>
                        <TextField
                            margin="normal"
                            required
                            fullWidth
                            label="Email Address"
                            name="email"
                            autoComplete="email"
                            value={registerData.email}
                            onChange={handleRegisterChange}
                            disabled={loading}
                        />
                        <TextField
                            margin="normal"
                            required
                            fullWidth
                            label="Mobile Number"
                            name="mobile"
                            autoComplete="tel"
                            value={registerData.mobile}
                            onChange={handleRegisterChange}
                            disabled={loading}
                        />
                        <TextField
                            margin="normal"
                            required
                            fullWidth
                            label="Password"
                            name="password"
                            type={showRegisterPassword ? 'text' : 'password'}
                            value={registerData.password}
                            onChange={handleRegisterChange}
                            disabled={loading}
                            slotProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton
                                            onClick={() => setShowRegisterPassword(!showRegisterPassword)}
                                            edge="end"
                                        >
                                            {showRegisterPassword ? <VisibilityOff /> : <Visibility />}
                                        </IconButton>
                                    </InputAdornment>
                                ),
                            }}
                        />
                        <TextField
                            margin="normal"
                            required
                            fullWidth
                            label="Confirm Password"
                            name="confirmPassword"
                            type={showConfirmPassword ? 'text' : 'password'}
                            value={registerData.confirmPassword}
                            onChange={handleRegisterChange}
                            disabled={loading}
                            slotProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton
                                            onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                            edge="end"
                                        >
                                            {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                                        </IconButton>
                                    </InputAdornment>
                                ),
                            }}
                        />

                        {/* Terms and Conditions Checkbox */}
                        <Box sx={{ mt: 2, mb: 1 }}>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={acceptTerms}
                                        onChange={handleTermsChange}
                                        disabled={loading}
                                        color="primary"
                                    />
                                }
                                label={
                                    <Typography variant="body2">
                                        I agree to the{' '}
                                        <Link
                                            onClick={() => navigate("/terms-conditions")}
                                            underline="hover"
                                            color="primary"
                                            style={{ cursor: 'pointer' }}
                                        >
                                            Terms and Conditions
                                        </Link>
                                        {/* {' '}and{' '}
                                        <Link
                                            onClick={() => navigate("/privacy-policy")}
                                            underline="hover"
                                            color="primary"
                                            style={{ cursor: 'pointer' }}
                                        >
                                            Privacy Policy
                                        </Link> */}
                                    </Typography>
                                }
                            />
                        </Box>
                    </Box>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} disabled={loading}>
                    Cancel
                </Button>
                <Button
                    onClick={tab === 0 ? handleLogin : handleRegister}
                    variant="contained"
                    disabled={loading}
                    startIcon={loading && <CircularProgress size={20} />}
                >
                    {tab === 0 ? 'Login' : 'Register'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default AuthDialog;